import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/redux/Store';
import { getSyncStatus, syncSingleProfile, syncAllProfiles } from '@/redux/thunks/adminThunks';
import { clearSyncError, clearLastSyncResult } from '@/redux/slices/adminSlice';
import { toast } from 'react-toastify';
import { LoadingSpinner } from '@/components';
import './page.scss';

const AdminSyncPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { syncStatus, syncLoading, syncError, lastSyncResult } = useAppSelector((state) => state.admin);
  const [selectedProfileId, setSelectedProfileId] = useState<string>('');

  useEffect(() => {
    // Load initial sync status
    dispatch(getSyncStatus());
  }, [dispatch]);

  useEffect(() => {
    if (syncError) {
      toast.error(syncError);
      dispatch(clearSyncError());
    }
  }, [syncError, dispatch]);

  const handleRefreshStatus = async () => {
    try {
      await dispatch(getSyncStatus()).unwrap();
      toast.success('Sync status refreshed');
    } catch (error) {
      // Error handled by slice
    }
  };

  const handleSyncSingle = async () => {
    const profileId = parseInt(selectedProfileId);
    if (isNaN(profileId) || profileId <= 0) {
      toast.error('Please enter a valid profile ID');
      return;
    }

    try {
      const result = await dispatch(syncSingleProfile(profileId)).unwrap();
      toast.success(result.message);
      // Refresh status after sync
      dispatch(getSyncStatus());
      setSelectedProfileId('');
    } catch (error) {
      // Error handled by slice
    }
  };

  const handleSyncAll = async () => {
    if (!confirm('This will sync ALL profiles from the blockchain. This may take a while. Continue?')) {
      return;
    }

    try {
      const result = await dispatch(syncAllProfiles()).unwrap();
      toast.success(`Synced ${result.results.synced} profiles. ${result.results.mismatches} mismatches fixed.`);
      // Refresh status after sync
      dispatch(getSyncStatus());
    } catch (error) {
      // Error handled by slice
    }
  };

  const handleSyncOutOfSyncProfile = async (profileId: number) => {
    try {
      const result = await dispatch(syncSingleProfile(profileId)).unwrap();
      toast.success(result.message);
      // Refresh status after sync
      dispatch(getSyncStatus());
    } catch (error) {
      // Error handled by slice
    }
  };

  return (
    <div className="admin-sync-page">
      <div className="admin-sync-container">
        <h1>Blockchain Sync Manager</h1>
        <p className="subtitle">Manage synchronization between database and blockchain</p>

        {/* Action Buttons */}
        <div className="sync-actions">
          <div className="action-card">
            <h2>Sync Single Profile</h2>
            <p>Sync a specific profile by ID</p>
            <div className="input-group">
              <input
                type="number"
                placeholder="Profile ID"
                value={selectedProfileId}
                onChange={(e) => setSelectedProfileId(e.target.value)}
                disabled={syncLoading}
              />
              <button
                onClick={handleSyncSingle}
                disabled={syncLoading || !selectedProfileId}
                className="btn-primary"
              >
                {syncLoading ? 'Syncing...' : 'Sync Profile'}
              </button>
            </div>
          </div>

          <div className="action-card">
            <h2>Sync All Profiles</h2>
            <p>Sync all profiles from blockchain to database</p>
            <button
              onClick={handleSyncAll}
              disabled={syncLoading}
              className="btn-danger"
            >
              {syncLoading ? 'Syncing...' : 'Sync All Profiles'}
            </button>
          </div>

          <div className="action-card">
            <h2>Refresh Status</h2>
            <p>Check current sync status</p>
            <button
              onClick={handleRefreshStatus}
              disabled={syncLoading}
              className="btn-secondary"
            >
              {syncLoading ? 'Loading...' : 'Refresh Status'}
            </button>
          </div>
        </div>

        {/* Sync Status */}
        {syncLoading && !syncStatus && <LoadingSpinner size="large" message="Loading sync status..." />}

        {syncStatus && (
          <div className="sync-status">
            <h2>Sync Status</h2>
            <div className="status-summary">
              <div className="stat-card">
                <div className="stat-value">{syncStatus.summary.total}</div>
                <div className="stat-label">Total Profiles</div>
              </div>
              <div className="stat-card success">
                <div className="stat-value">{syncStatus.summary.inSync}</div>
                <div className="stat-label">In Sync</div>
              </div>
              <div className="stat-card warning">
                <div className="stat-value">{syncStatus.summary.outOfSync}</div>
                <div className="stat-label">Out of Sync</div>
              </div>
              <div className="stat-card error">
                <div className="stat-value">{syncStatus.summary.errors}</div>
                <div className="stat-label">Errors</div>
              </div>
            </div>

            {/* Out of Sync Profiles */}
            {syncStatus.outOfSync.length > 0 && (
              <div className="out-of-sync-section">
                <h3>Out of Sync Profiles</h3>
                <div className="profiles-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Profile ID</th>
                        <th>Database Price</th>
                        <th>Blockchain Price</th>
                        <th>Difference</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {syncStatus.outOfSync.map((profile) => (
                        <tr key={profile.profileId}>
                          <td>{profile.profileId}</td>
                          <td>{profile.databasePrice} KLYX</td>
                          <td>{profile.blockchainPrice} KLYX</td>
                          <td className="difference">{profile.difference} KLYX</td>
                          <td>
                            <button
                              onClick={() => handleSyncOutOfSyncProfile(profile.profileId)}
                              disabled={syncLoading}
                              className="btn-sm btn-primary"
                            >
                              Sync
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Errors */}
            {syncStatus.errors.length > 0 && (
              <div className="errors-section">
                <h3>Errors</h3>
                <ul>
                  {syncStatus.errors.map((error, index) => (
                    <li key={index}>
                      Profile {error.profileId}: {error.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Last Sync Result */}
        {lastSyncResult && (
          <div className="sync-result">
            <h2>Last Sync Result</h2>
            <div className="result-summary">
              <p>
                <strong>Total:</strong> {lastSyncResult.results.total} profiles
              </p>
              <p>
                <strong>Synced:</strong> {lastSyncResult.results.synced} profiles
              </p>
              <p>
                <strong>Mismatches Fixed:</strong> {lastSyncResult.results.mismatches}
              </p>
              <p>
                <strong>Errors:</strong> {lastSyncResult.results.errors}
              </p>
            </div>

            {lastSyncResult.results.details.some(d => d.status === 'updated') && (
              <div className="updated-profiles">
                <h3>Updated Profiles</h3>
                <ul>
                  {lastSyncResult.results.details
                    .filter(d => d.status === 'updated')
                    .map((detail) => (
                      <li key={detail.profileId}>
                        Profile {detail.profileId}: {detail.oldPrice} → {detail.newPrice} KLYX
                      </li>
                    ))}
                </ul>
              </div>
            )}

            <button onClick={() => dispatch(clearLastSyncResult())} className="btn-secondary">
              Clear Results
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSyncPage;
